import { ChevronDown } from "lucide-react";
import { useState } from "react";
import { NavLink, useLocation } from "react-router";

import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";

import { useNavAccess } from "../use-nav-access";

import type { NavItem } from "../nav-config";

type ActiveMatcher = (pathname: string, to: string) => boolean;

const startsWith: ActiveMatcher = (pathname, to) => pathname.startsWith(to);

function NavItemLink({
  item,
  iconClass,
  isActive,
}: {
  item: NavItem;
  iconClass: string;
  isActive: boolean;
}) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      className={cn(
        "flex items-center gap-2",
        isActive && "font-medium text-primary",
      )}
    >
      <Icon className={iconClass} />
      {item.label}
    </NavLink>
  );
}

/** Top-level, non-collapsible list of links (Dashboard, Pengaturan Global…). */
export function SidebarFlatItems({
  items,
  label,
}: {
  items: NavItem[];
  /** Renders a collapsible group when given; a plain list otherwise. */
  label?: string;
}) {
  const { pathname } = useLocation();
  const { isAllowed } = useNavAccess();
  const [open, setOpen] = useState(true);
  const visible = items.filter(isAllowed);
  if (visible.length === 0) return null;
  const list = (
    <SidebarGroupContent>
      <SidebarMenu className="gap-1.5">
        {visible.map((item) => {
          const isActive = startsWith(pathname, item.to);
          return (
            <SidebarMenuItem key={item.to}>
              <SidebarMenuButton asChild isActive={isActive}>
                <NavItemLink
                  item={item}
                  iconClass="h-4 w-4"
                  isActive={isActive}
                />
              </SidebarMenuButton>
            </SidebarMenuItem>
          );
        })}
      </SidebarMenu>
    </SidebarGroupContent>
  );

  if (!label) return <SidebarGroup>{list}</SidebarGroup>;
  return (
    <SidebarGroup>
      <GroupLabel
        label={label}
        open={open}
        onToggle={() => setOpen((o) => !o)}
      />
      {open && list}
    </SidebarGroup>
  );
}

/** Collapsible group whose links render as an indented sub-menu. */
export function SidebarSubGroup({
  label,
  items,
  isActive = startsWith,
}: {
  label: string;
  items: NavItem[];
  isActive?: ActiveMatcher;
}) {
  const { pathname } = useLocation();
  const { isAllowed } = useNavAccess();
  const [open, setOpen] = useState(true);
  const visible = items.filter(isAllowed);
  if (visible.length === 0) return null;
  return (
    <SidebarGroup>
      <GroupLabel
        label={label}
        open={open}
        onToggle={() => setOpen((o) => !o)}
      />
      {open && (
        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuSub className="gap-1.5 py-1.5">
                {visible.map((item) => {
                  const active = isActive(pathname, item.to);
                  return (
                    <SidebarMenuSubItem key={item.to}>
                      <SidebarMenuSubButton
                        asChild
                        isActive={active}
                        className="h-8"
                      >
                        <NavItemLink
                          item={item}
                          iconClass="h-3.5 w-3.5"
                          isActive={active}
                        />
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  );
                })}
              </SidebarMenuSub>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      )}
    </SidebarGroup>
  );
}

function GroupLabel({
  label,
  open,
  onToggle,
}: {
  label: string;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <SidebarGroupLabel
      className="flex cursor-pointer items-center justify-between"
      onClick={onToggle}
    >
      {label}
      <ChevronDown
        className={cn("h-4 w-4 transition-transform", open && "rotate-180")}
      />
    </SidebarGroupLabel>
  );
}
